type BraveNavigator = Navigator & {
  brave?: { isBrave?: () => Promise<boolean> };
};

type NavigatorWithUAData = Navigator & {
  userAgentData?: {
    getHighEntropyValues?: (
      hints: string[]
    ) => Promise<{ brands?: { brand: string; version: string }[] }>;
  };
};

/** Max wait for Client Hints; Safari/WebKit may expose userAgentData but never settle getHighEntropyValues. */
const CLIENT_HINTS_TIMEOUT_MS = 400;

/** Desktop Safari (and WebKit shells on Mac): Apple vendor, not iOS/Android, no Chrome/Edg/etc. */
function isAppleVendorDesktopWithoutChromeEngine(ua: string, vendor: string | undefined): boolean {
  if (vendor !== 'Apple Computer, Inc.') return false;
  if (/iPhone|iPad|iPod|Android/i.test(ua)) return false;
  if (/Chrome\/|CriOS|Edg\/|OPR\/|FxiOS|Firefox\//i.test(ua)) return false;
  return true;
}

/**
 * macOS WebKit without a Chrome-class engine token (covers reduced UA that omits "Safari" / "Version/").
 * iOS UA contains "like Mac OS X" — excluded up front via iPhone|iPad|iPod.
 */
function isAppleWebKitMacWithoutChromeEngine(ua: string): boolean {
  if (/iPhone|iPad|iPod|Android/i.test(ua)) return false;
  if (!/AppleWebKit/i.test(ua)) return false;
  if (!/(Macintosh|Mac OS X)/i.test(ua)) return false;
  if (/Chrome\/|Chromium\/|Edg\/|OPR\/|Firefox\/|CriOS|FxiOS/i.test(ua)) return false;
  return true;
}

function isDesktopSafariByUa(ua: string): boolean {
  return /Version\/[\d.]+\s+Safari\//i.test(ua) && !/Chrome\//i.test(ua);
}

/**
 * Synchronous, UA-only: true if the user is clearly not Google Chrome (show hint/toast immediately).
 * `vendor` defaults to `navigator.vendor` when omitted (browser only).
 */
export function shouldPromptUseChromeFromUserAgentOnly(
  ua: string,
  vendor: string | undefined = typeof navigator !== 'undefined' ? navigator.vendor : undefined
): boolean {
  if (/Firefox|FxiOS/i.test(ua)) return true;
  if (/Edg\/|EdgA|EdgiOS/i.test(ua)) return true;
  if (/OPR\/|Opera/i.test(ua)) return true;
  if (/SamsungBrowser/i.test(ua)) return true;

  if (/iPhone|iPad|iPod/i.test(ua)) {
    return !/CriOS/i.test(ua);
  }

  if (/Android/i.test(ua)) {
    if (!/Chrome\//i.test(ua)) return true;
    if (/wv\)/i.test(ua)) return true;
    return false;
  }

  if (isAppleVendorDesktopWithoutChromeEngine(ua, vendor)) return true;
  if (isAppleWebKitMacWithoutChromeEngine(ua)) return true;

  if (isDesktopSafariByUa(ua)) return true;

  if (/Safari/i.test(ua) && !/Chrome|Chromium|CriOS/i.test(ua)) {
    return true;
  }

  return false;
}

/**
 * True when this is clearly not stock Google Chrome — overrides misleading Client Hints "Google Chrome" brand.
 */
function isDefinitelyNotChromeFromUa(
  ua: string,
  vendor: string | undefined = typeof navigator !== 'undefined' ? navigator.vendor : undefined
): boolean {
  return shouldPromptUseChromeFromUserAgentOnly(ua, vendor);
}

/**
 * Best-effort detection of Google Chrome (not other Chromium browsers where we can tell).
 * Used to gate the web app per product requirements.
 */
export async function isGoogleChrome(): Promise<boolean> {
  if (typeof navigator === 'undefined') return true;

  const ua = navigator.userAgent;
  const vendor = navigator.vendor;

  if (/Firefox|FxiOS/i.test(ua)) return false;
  if (/Edg\/|EdgA|EdgiOS/i.test(ua)) return false;
  if (/OPR\/|Opera/i.test(ua)) return false;
  if (/SamsungBrowser/i.test(ua)) return false;

  if (/iPhone|iPad|iPod/i.test(ua)) {
    return /CriOS/i.test(ua);
  }

  if (/Android/i.test(ua)) {
    if (!/Chrome/i.test(ua)) return false;
    if (/wv\)/i.test(ua)) return false;
    return true;
  }

  if (isAppleVendorDesktopWithoutChromeEngine(ua, vendor)) return false;
  if (isAppleWebKitMacWithoutChromeEngine(ua)) return false;

  if (isDesktopSafariByUa(ua)) {
    return false;
  }

  if (/Safari/i.test(ua) && !/Chrome|Chromium|CriOS/i.test(ua)) {
    return false;
  }

  const uaData = (navigator as NavigatorWithUAData).userAgentData;
  if (uaData?.getHighEntropyValues) {
    try {
      const data = await Promise.race([
        uaData.getHighEntropyValues(['brands']),
        new Promise<undefined>((resolve) => {
          window.setTimeout(() => resolve(undefined), CLIENT_HINTS_TIMEOUT_MS);
        })
      ]);

      const brands = data?.brands;
      if (brands?.length) {
        const hasGoogleChrome = brands.some((b: { brand: string }) => b.brand === 'Google Chrome');
        const chromiumOnly =
          brands.some((b: { brand: string }) => b.brand === 'Chromium') && !hasGoogleChrome;
        if (chromiumOnly) return false;
        if (hasGoogleChrome) {
          if (isDefinitelyNotChromeFromUa(ua, vendor)) return false;
          const brave = (navigator as BraveNavigator).brave;
          if (brave?.isBrave) {
            try {
              if (await brave.isBrave()) return false;
            } catch {
              /* ignore */
            }
          }
          return true;
        }
      }
    } catch {
      /* fall through to UA */
    }
  }

  if (!/Chrome/i.test(ua)) return false;
  if (/Edg|OPR|Opera|SamsungBrowser/i.test(ua)) return false;

  const brave = (navigator as BraveNavigator).brave;
  if (brave?.isBrave) {
    try {
      if (await brave.isBrave()) return false;
    } catch {
      /* ignore */
    }
  }

  return true;
}

/**
 * True when the current platform is macOS (not iOS).
 * The `googlechrome://` URL scheme is only registered by Chrome on macOS.
 */
export function isMacOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return false;
  // userAgentData.platform is the modern API; fall back to legacy navigator.platform
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  const platform = nav.userAgentData?.platform ?? '';
  return /mac/i.test(platform) || /(Macintosh|Mac OS X)/i.test(ua);
}

/**
 * Builds a hand-off URL so Safari/macOS opens the page in Google Chrome.
 *
 * Chrome on macOS registers `googlechrome://` (http) and `googlechromes://` (https).
 * Simply swap the scheme prefix — host, path, query, and fragment are preserved as-is.
 */
export function chromeDeepLinkForUrl(pageUrl: string): string {
  try {
    const url = new URL(pageUrl);
    if (url.protocol === 'https:') {
      return 'googlechromes://' + url.href.slice('https://'.length);
    }
    return 'googlechrome://' + url.href.slice('http://'.length);
  } catch {
    return 'googlechrome://' + pageUrl.replace(/^https?:\/\//, '');
  }
}
